/*
 * @fileOverView Filters component definition
 *
 * This isn't demonstrated within Fractal because there's no back-end to
 * interact with.
 */

import Vue from 'vue';
import QueryStringPairs from 'utils/QueryStringPairs';

/**
 * [Filters Vue]
 * @type {class}
 */
Vue.component('filters-vue', {
  props: [
    'id',
    'endpoint',
    'filter',
  ],

  data: function() {
    return {
      count: false,
      loading: false,
      values: {}
    }
  },

  mounted() {
    // Give the filters Vuex store the baseline information about this list.
    this.$store.commit('filters/createInstance', this.id);
    this.$store.dispatch('filters/setEndpoint', {
      id: this.id,
      endpoint: this.endpoint
    });

    // Tracks the values, count, and loading for this filter set. Subscribing to
    // the store is needed because computed properties won't always recognize
    // property changes for a specific set.
    this.$store.subscribe((mutation, state) => {
      if (typeof mutation.payload.id !== 'undefined' && mutation.payload.id === this.id) {
        if (mutation.type === 'filters/setValue') {
          this.values = this.$store.state.filters.sets[this.id].values;
        }
        else if (mutation.type === 'filters/setCount') {
          this.count = mutation.payload.count;
        }
        else if (mutation.type === 'filters/setLoading') {
          this.loading = mutation.payload.loading;
        }
      }
    });

    if (typeof this.skipInitialUrlQuery === 'function' && typeof this.setFiltersFromUrlQuery === 'function') {
      if (!this.skipInitialUrlQuery()) {
        this.setFiltersFromUrlQuery();
      }
    }
  },

  methods: {
    /**
     * Clears all selected filter values, resetting them to their default value.
     */
    clearFilters: function () {
      // Ensure there isn't a request made until all filters have been cleared.
      this.$store.commit('filters/setLocked', {
        id: this.id,
        locked: true
      });

      for (let filterName in this.values) {
        if (this.values.hasOwnProperty(filterName)) {
          this.resetFilter(filterName);
        }
      }

      // All filters have been cleared. Request the new results.
      this.$nextTick(() => {
        this.$store.commit('filters/setLocked', {
          id: this.id,
          locked: false
        });
        this.$store.dispatch('filters/update', this.id);
      });
    },

    /**
     * Signifies if the filter set should ignore the URL query on initial load.
     */
    skipInitialUrlQuery() {
      // QueryStringPairs returns errors when executing without URL parameters.
      // So, it is better to catch any error when it is executed.
      try {
        const queryValues = QueryStringPairs();
        // If the query matches the filter name
        // avoid resetting the results.
        if (queryValues.hasOwnProperty(this.filter)) {
          return true;
        }

        // The search results always have a query on page load (?query=) if
        // something was searched, which is initially handled server-side. If the
        // filters responded to this on load, they'd make an unnecessary request.
        if (window.location.pathname === '/search') {
          return true;
        }
      } catch (error) {
        return false;
      }
    },

    /**
     * Resets an individual filter to its default value.
     */
    resetFilter: function (filterName) {
      if (typeof this.$refs.filtersModal.$refs[filterName + 'Filter'] !== 'undefined') {
        // Calls the reset method on the filter's inner element. This is a bit
        // funky due to how all the filter pieces communicate with eachother.
        this.$refs.filtersModal.$refs[filterName + 'Filter'].$refs.filterElement.reset();
      }
      else {
        // For non-filterable elements committed directly to the store from a
        // URL query parameter. See setFiltersFromUrlQuery() for more info.
        this.$store.dispatch('filters/setValue', {
          id: this.id,
          name: filterName,
          value: false
        });
      }
    },

    /**
     * Sets the filters to the values specified in the URL query parameters.
     */
    setFiltersFromUrlQuery: function () {
      const queryValues = QueryStringPairs();

      if (queryValues) {
        // Ensure there isn't a request made until all filters have been set.
        this.$store.commit('filters/setLocked', {
          id: this.id,
          locked: true
        });

        // There is/isn't a modal window within the filters element depending on
        // if there are one or more filters. This helps find the child filters.
        let parentElement = this;
        if (typeof this.$refs.filtersModal !== 'undefined') {
          parentElement = this.$refs.filtersModal;
        }

        for (var filterName in queryValues) {
          if (queryValues.hasOwnProperty(filterName)) {
            const filterValue = decodeURIComponent(queryValues[filterName]);

            if (typeof parentElement.$refs[filterName + 'Filter'] !== 'undefined') {
              // Due to how all the filter pieces communicate, we have to dig deep
              // and call its set method, so that it updates its own display and
              // emits its change back upward for the filters to respond to.
              parentElement.$refs[filterName + 'Filter'].$refs.filterElement.set(filterValue);
            }
            else {
              // Some things can be mysteriously filtered via a URL query
              // parameter even if it doesn't have a filter on the page, such as
              // within Funded Studies. If a value is passed without a filter,
              // commit it directly into the store for this purpose.
              this.$store.dispatch('filters/setValue', {
                id: this.id,
                name: filterName,
                value: filterValue
              });
            }
          }
        }

        // All filters have been set. Request the new results.
        this.$nextTick(() => {
          this.$store.commit('filters/setLocked', {
            id: this.id,
            locked: false
          });
          this.$store.dispatch('filters/update', this.id);
        });
      }
    }
  }
})
